import React from "react";
import { Box, Stack, Button, Icon } from "@chakra-ui/react";
import { MdAcUnit, MdLockOpen, MdGavel } from "react-icons/md";

/**
 * Builds the Chakra style props for the freeze/unfreeze action button.
 * @param {boolean} isFrozen - Whether the wallet is currently frozen
 * @param {boolean} isMobile - Whether the viewport is mobile-sized
 * @returns {Object} Chakra style props for the freeze button
 */
function buildFreezeButtonProps(isFrozen, isMobile) {
    return {
        background: isFrozen ? "rgba(76, 175, 80, 0.15)" : "rgba(255, 152, 0, 0.15)",
        backdropFilter: "blur(10px)",
        color: isFrozen ? "#4caf50" : "#ff9800",
        border: `1px solid ${isFrozen ? "rgba(76, 175, 80, 0.4)" : "rgba(255, 152, 0, 0.4)"}`,
        fontWeight: 600,
        fontSize: isMobile ? "0.7rem" : "0.75rem",
        padding: isMobile ? "7px 14px" : "8px 16px",
        borderRadius: "8px",
        transition: "all 0.3s ease",
        _hover: {
            background: isFrozen ? "rgba(76, 175, 80, 0.25)" : "rgba(255, 152, 0, 0.25)",
            transform: "translateY(-1px)",
            boxShadow: `0 4px 12px ${isFrozen ? "rgba(76, 175, 80, 0.2)" : "rgba(255, 152, 0, 0.2)"}`
        }
    };
}

/** Chakra style props for the confiscate action button. */
const confiscateActionProps = {
    background: "rgba(244, 67, 54, 0.15)",
    backdropFilter: "blur(10px)",
    color: "#f44336",
    border: "1px solid rgba(244, 67, 54, 0.4)",
    fontWeight: 600,
    borderRadius: "8px",
    transition: "all 0.3s ease",
    _hover: {
        background: "rgba(244, 67, 54, 0.25)",
        transform: "translateY(-1px)",
        boxShadow: "0 4px 12px rgba(244, 67, 54, 0.2)"
    },
    _disabled: {
        background: "rgba(244, 67, 54, 0.05)",
        color: "rgba(244, 67, 54, 0.3)",
        border: "1px solid rgba(244, 67, 54, 0.1)"
    }
};

/**
 * Renders freeze/unfreeze and confiscate action buttons for the wallet.
 * @param {Object} props
 * @param {Object} props.wallet - Wallet data with frozen status and balance
 * @param {number} props.adminRight - Admin right level (1=admin, 2=super admin)
 * @param {Function} props.onFreezeClick - Handler to open freeze dialog
 * @param {Function} props.onConfiscateClick - Handler to open confiscate dialog
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function WalletActions({ wallet, adminRight, onFreezeClick, onConfiscateClick, isMobile }) {
    const isFrozen = wallet?.frozen;

    return (
        <Box mt={6}>
            <Stack direction={isMobile ? "column" : "row"} spacing={3}>
                <Button
                    width="100%"
                    leftIcon={<Icon as={isFrozen ? MdLockOpen : MdAcUnit} />}
                    onClick={onFreezeClick}
                    {...buildFreezeButtonProps(isFrozen, isMobile)}
                >
                    {isFrozen ? "Unfreeze wallet" : "Freeze wallet"}
                </Button>
                <ConfiscateButton
                    wallet={wallet}
                    adminRight={adminRight}
                    onClick={onConfiscateClick}
                    isMobile={isMobile}
                />
            </Stack>
        </Box>
    );
}

/**
 * Renders the confiscate button for super admins only.
 * @param {Object} props
 * @param {Object} props.wallet - Wallet data with balance
 * @param {number} props.adminRight - Admin right level (2 = super admin)
 * @param {Function} props.onClick - Click handler to open confiscate dialog
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement|null}
 */
function ConfiscateButton({ wallet, adminRight, onClick, isMobile }) {
    if (adminRight !== 2) return null;

    return (
        <Button
            width="100%"
            leftIcon={<Icon as={MdGavel} />}
            onClick={onClick}
            isDisabled={wallet?.balance === 0}
            {...confiscateActionProps}
            fontSize={isMobile ? "0.7rem" : "0.75rem"}
            padding={isMobile ? "7px 14px" : "8px 16px"}
        >
            Confiscate balance
        </Button>
    );
}

export default WalletActions;
